/**
 * Persistence helpers for the POS UI layer. Consumers should rely on these utilities instead of
 * accessing `localStorage` directly to ensure stored payloads stay compatible across versions.
 */
import {
  POS_UI_PREFERENCES_KEY,
  POS_UI_PREFERENCES_VERSION,
  POS_UI_PREFERENCES_EXPIRATION_MS,
  createDefaultPosUiPreferences,
  type PosShortcutAction,
  type PosShortcutMap,
  type PosUiCollapsedState,
  type PosUiPreferences,
  type PosUiPreferencesEnvelope,
} from './types';

export type {
  PosUiPreferences,
  PosUiPreferencesEnvelope,
  PosShortcutAction,
  PosShortcutMap,
} from './types';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const memoryStore = new Map<string, string>();

const memoryStorage: StorageLike = {
  getItem: (key) => (memoryStore.has(key) ? memoryStore.get(key)! : null),
  setItem: (key, value) => {
    memoryStore.set(key, value);
  },
  removeItem: (key) => {
    memoryStore.delete(key);
  },
};

let cachedStorage: StorageLike | null = null;

const detectStorage = (): StorageLike => {
  if (typeof window === 'undefined') {
    return memoryStorage;
  }

  try {
    const storage = window.localStorage;
    if (!storage) {
      return memoryStorage;
    }
    const probeKey = `${POS_UI_PREFERENCES_KEY}::probe`;
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return storage;
  } catch (error) {
    return memoryStorage;
  }
};

const getActiveStorage = (): StorageLike => {
  if (cachedStorage) {
    return cachedStorage;
  }
  cachedStorage = detectStorage();
  return cachedStorage;
};

const withStorage = <T>(operation: (storage: StorageLike) => T, fallback: () => T): T => {
  try {
    return operation(getActiveStorage());
  } catch (error) {
    cachedStorage = memoryStorage;
    return fallback();
  }
};

const safeParseEnvelope = (raw: string | null): PosUiPreferencesEnvelope | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed as PosUiPreferencesEnvelope;
  } catch (error) {
    return null;
  }
};

type LegacyCollapsedState = {
  lineNotes?: boolean;
  paymentsPanel?: boolean;
  discountsPanel?: boolean;
};

type RawCollapsedState = Partial<PosUiCollapsedState> & LegacyCollapsedState;

const mergeCollapsed = (
  base: PosUiPreferences['collapsed'],
  override?: RawCollapsedState,
): PosUiPreferences['collapsed'] => ({
  customer: override?.customer ?? base.customer,
  payments:
    override?.payments ??
    (typeof override?.paymentsPanel === 'boolean' ? override.paymentsPanel : base.payments),
  shipping: override?.shipping ?? base.shipping,
  discounts:
    override?.discounts ??
    (typeof override?.discountsPanel === 'boolean' ? override.discountsPanel : base.discounts),
});

const mergePayments = (
  base: PosUiPreferences['payments'],
  override?: Partial<PosUiPreferences['payments']>,
  legacyPreferred?: number | null,
): PosUiPreferences['payments'] => {
  const hasLegacy = legacyPreferred !== undefined;
  const preferred =
    override?.preferredMethodId ?? (hasLegacy ? legacyPreferred ?? null : undefined);
  return {
    preferredMethodId:
      preferred !== undefined ? preferred : base.preferredMethodId ?? null,
  };
};

const mergeBottomBar = (
  base: PosUiPreferences['bottomBar'],
  override?: Partial<PosUiPreferences['bottomBar']>,
): PosUiPreferences['bottomBar'] => ({
  isPinned: override?.isPinned ?? base.isPinned,
  showQuickActions: override?.showQuickActions ?? base.showQuickActions,
});

const mergeLayout = (
  base: PosUiPreferences['layout'],
  override?: Partial<PosUiPreferences['layout']>,
): PosUiPreferences['layout'] => ({
  mobileMode: override?.mobileMode ?? base.mobileMode,
});

const mergeShortcuts = (
  base: PosShortcutMap,
  override?: Partial<PosShortcutMap>,
): PosShortcutMap => {
  const merged = { ...base } as PosShortcutMap;
  (Object.keys(base) as PosShortcutAction[]).forEach((action) => {
    if (override?.[action]) {
      merged[action] = override[action]!.map((definition) => ({ ...definition }));
    }
  });
  return merged;
};

type LegacyPosUiPreferences = Partial<PosUiPreferences> & {
  collapsed?: RawCollapsedState;
  preferredPaymentMethodId?: number | null;
};

const normalizePreferences = (preferences: LegacyPosUiPreferences | undefined): PosUiPreferences => {
  const defaults = createDefaultPosUiPreferences();
  if (!preferences) {
    return defaults;
  }
  return {
    lastVariantByItemId: { ...defaults.lastVariantByItemId, ...(preferences.lastVariantByItemId ?? {}) },
    lastProfessionalByItemId: {
      ...defaults.lastProfessionalByItemId,
      ...(preferences.lastProfessionalByItemId ?? {}),
    },
    collapsed: mergeCollapsed(defaults.collapsed, preferences.collapsed),
    payments: mergePayments(defaults.payments, preferences.payments, preferences.preferredPaymentMethodId),
    orderNoteDraft: preferences.orderNoteDraft ?? defaults.orderNoteDraft,
    lastCustomerId:
      typeof preferences.lastCustomerId === 'number'
        ? preferences.lastCustomerId
        : preferences.lastCustomerId === null
          ? null
          : defaults.lastCustomerId,
    bottomBar: mergeBottomBar(defaults.bottomBar, preferences.bottomBar),
    layout: mergeLayout(defaults.layout, preferences.layout),
    shortcuts: mergeShortcuts(defaults.shortcuts, preferences.shortcuts),
  };
};

const buildEnvelope = (preferences: PosUiPreferences): PosUiPreferencesEnvelope => ({
  version: POS_UI_PREFERENCES_VERSION,
  updatedAt: Date.now(),
  preferences,
});

/**
 * Reads the persisted preferences envelope and returns a sanitized snapshot that callers can use
 * safely. Unknown or outdated fields are discarded while preserving stable contracts.
 */
export const loadPreferences = (): PosUiPreferences => {
  const raw = withStorage(
    (storage) => storage.getItem(POS_UI_PREFERENCES_KEY),
    () => memoryStorage.getItem(POS_UI_PREFERENCES_KEY),
  );
  const envelope = safeParseEnvelope(raw);
  if (!envelope) {
    return createDefaultPosUiPreferences();
  }

  if (Date.now() - envelope.updatedAt > POS_UI_PREFERENCES_EXPIRATION_MS) {
    clearPreferences();
    return createDefaultPosUiPreferences();
  }

  if (envelope.version !== POS_UI_PREFERENCES_VERSION) {
    const migrated = normalizePreferences(envelope.preferences);
    persistPreferences(migrated);
    return migrated;
  }

  return normalizePreferences(envelope.preferences);
};

/**
 * Persists a preferences snapshot after normalizing it, ensuring migrations are applied and the
 * data structure stays forward compatible.
 */
export const persistPreferences = (preferences: PosUiPreferences): void => {
  const normalized = normalizePreferences(preferences);
  const envelope = buildEnvelope(normalized);
  const serialized = JSON.stringify(envelope);

  withStorage(
    (storage) => {
      storage.setItem(POS_UI_PREFERENCES_KEY, serialized);
      return undefined;
    },
    () => {
      memoryStorage.setItem(POS_UI_PREFERENCES_KEY, serialized);
    },
  );
};

/**
 * Updates the stored preferences atomically by reading the latest value, applying a transformer
 * and writing the normalized result back.
 */
export const updatePersistedPreferences = (
  updater: (previous: PosUiPreferences) => PosUiPreferences,
): PosUiPreferences => {
  const previous = loadPreferences();
  const next = updater(previous);
  persistPreferences(next);
  return next;
};

/**
 * Removes any persisted preferences from storage. Consumers typically call this when resetting the
 * POS shell to defaults.
 */
export const clearPreferences = (): void => {
  withStorage(
    (storage) => {
      storage.removeItem(POS_UI_PREFERENCES_KEY);
      return undefined;
    },
    () => {
      memoryStorage.removeItem(POS_UI_PREFERENCES_KEY);
    },
  );
};

export const __unsafeResetPreferencesForTests = (): void => {
  memoryStore.clear();
  cachedStorage = null;
};
